import React from 'react';
import { ToggleButtonGroup, ToggleButton, ToggleButtonGroupProps } from '@mui/material';
import { styled } from '@mui/material/styles';

const CustomToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  height: '56px',
  width: '100%',
  '& .MuiToggleButton-root': {
    height: '42px',
    fontSize: '1rem',
    fontWeight: 500,
    textTransform: 'none',
    color: '#666',
    border: '1px solid #E0E0E0',
    fontFamily: "'Roboto', sans-serif",
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',

    '& .toggle-button-icon': {
      fontSize: '1.1rem',
      display: 'flex',
      alignItems: 'center',
    },

    '&.Mui-selected': {
      backgroundColor: '#89BD23',
      color: '#FFFFFF',
      fontWeight: 600,
      borderColor: '#89BD23',

      '&:hover': {
        backgroundColor: '#7AA61F',
        borderColor: '#7AA61F',
      },
    },

    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: '#BDBDBD',
    },

    '&.Mui-disabled': {
      opacity: 0.6,
      color: '#BDBDBD',
      backgroundColor: '#F5F5F5',
    },

    '&:focus': {
      outline: 'none',
      boxShadow: '0 0 0 2px rgba(137, 189, 35, 0.2)',
    },
  },
}));

export interface StyledToggleButtonGroupProps extends Omit<ToggleButtonGroupProps, 'children'> {
  /** Opções do toggle button */
  options: {
    value: string;
    label: string;
    disabled?: boolean;
    icon?: React.ReactNode;
    iconPosition?: 'start' | 'end';
  }[];
}

export const StyledToggleButtonGroup = ({
  options,
  ...props
}: StyledToggleButtonGroupProps) => {
  const renderButtonContent = (option: StyledToggleButtonGroupProps['options'][0]) => {
    if (!option.icon) {
      return option.label;
    }

    const iconElement = (
      <span className="toggle-button-icon">
        {option.icon}
      </span>
    );

    if (option.iconPosition === 'end') {
      return (
        <>
          {option.label}
          {iconElement}
        </>
      );
    }

    // Default: icon at start
    return (
      <>
        {iconElement}
        {option.label}
      </>
    );
  };

  return (
    <CustomToggleButtonGroup
      exclusive
      fullWidth
      {...props}
    >
      {options.map((option) => (
        <ToggleButton
          key={option.value}
          value={option.value}
          disabled={option.disabled}
        >
          {renderButtonContent(option)}
        </ToggleButton>
      ))}
    </CustomToggleButtonGroup>
  );
};
